import React, { FC, memo, useMemo, useState } from 'react';
import { CardContent, CollapsibleCard } from '@saucelabs/ds-card';
import { useQuery } from '@apollo/client';
import { Typography } from '@saucelabs/ds-typography';
import classnames from 'classnames';

import {
  BuildDetailsDiffExpandedFragFragment,
  BuildSnapshotThumbFragFragment,
  DiffStatus,
} from '../../graphql/__generated__/graphql';
import { graphql } from '../../graphql/__generated__';
import { BuildSnapshotThumb } from './BuildSnapshotThumb';
import { NotNullProperties } from '../../global/types';

export const BuildDetailsDiffExpandedFrag = graphql(`
  fragment BuildDetailsDiffExpandedFrag on Diff {
    id
    status
    baselineId
  }
`);

const GetBuildDetailsExpandedDiffDocument = graphql(/* GraphQL */ `
  query GetBuildDetailsExpandedDiffDocument ($ids: [UUID!]!) {
    diffs(filter: {id: {in: $ids}}) {
      nodes {
        ...BuildDetailsDiffExpandedFrag
      }
    }
  }
`);

const FOR_REVIEW_STATUSES: DiffStatus[] = [
  DiffStatus.Unapproved,
];

export const BuildTestCard: FC<BuildTestCardProps> = memo((props) => {
  const { test } = props;
  const [testTitle, diffs] = test;
  const [isOpen, setIsOpen] = useState(true);

  const diffIds = useMemo(() => (
    diffs.map((diff) => diff.id)
  ), [diffs]);

  const fullDiffs = useMemo(() => diffs
    .filter((diff): diff is NotNullProperties<BuildSnapshotThumbFragFragment, 'snapshot'> => (
      !!diff.snapshot
    )), [diffs]);

  const { data } = useQuery(GetBuildDetailsExpandedDiffDocument, {
    variables: {
      ids: diffIds,
    },
    // Uses a cache redirect in ApolloClient.tsx which allows us to query the cache for the
    // requested values, and observe for changes without making a network call the first time.
    fetchPolicy: 'cache-only',
  });
  const diffsWithStatus = data?.diffs?.nodes as BuildDetailsDiffExpandedFragFragment[] ?? [];

  const diffCount = diffIds.length;
  const forReviewCount: number = diffsWithStatus
    .filter((diff) => FOR_REVIEW_STATUSES.includes(diff.status)).length ?? 0;
  const newCount: number = diffsWithStatus
    .filter((diff) => !diff.baselineId).length ?? 0;

  return (
    <CollapsibleCard
      cardHeaderProps={{
        disableTypography: true,
      }}
      cardTitle={(
        <div className="w-full flex flex-row items-center my-auto">
          <Typography
            className="m-0"
            disableSpacing
            variant="heading-5"
          >
            {testTitle}
          </Typography>
          <Typography
            className="ml-auto text-medium-emphasis mr-1"
            variant="body-small"
          >
            <span className="font-semibold">
              {diffCount}
            </span>
            {' '}
            Snapshot
            {diffCount > 1 && 's'}
            {' '}
            |
            {' '}
            <span className="font-semibold">
              {forReviewCount}
            </span>
            {' '}
            For Review
            {newCount > 0 && (
              ` (${newCount} new)`
            )}
          </Typography>
        </div>
      )}
      isOpen={isOpen}
      onIsOpenChange={setIsOpen}
      spacing="condensed"
    >
      <CardContent>
        <div
          className={classnames('flex flex-row flex-wrap items-start -mx-1', {
            hidden: !isOpen,
          })}
        >
          {fullDiffs.map((diff) => (
            <BuildSnapshotThumb
              key={diff.id}
              diff={diff}
            />
          ))}
        </div>
      </CardContent>
    </CollapsibleCard>
  );
});

BuildTestCard.displayName = 'BuildTestCard';

export interface BuildTestCardProps {
  test: [string, BuildSnapshotThumbFragFragment[]];
}
